import {
  FilterDocument,
  GroupByMode,
  ID,
  LinkedItemSection,
  ReorderSectionUpdate,
  SortingMode,
} from '@griply/types';

type CustomTaskOrderKey = Exclude<
  GroupByMode,
  GroupByMode.None | GroupByMode.LifeArea
>;

type LinkedItemCustomTaskOrder = Partial<
  Record<CustomTaskOrderKey, Record<ID, ID[]> | null>
>;

// The ordering fields a goal and a life area share — the reorder write only touches these.
export type LinkedItemReorderContainer = {
  id: ID;
  sections: LinkedItemSection[] | null;
  taskSorting: ID[] | null;
  filterDocument?: FilterDocument | null;
  customTaskOrder?: LinkedItemCustomTaskOrder;
};

export type LinkedItemSectionReorderPatch = {
  id: ID;
  sections: LinkedItemSection[];
  taskSorting: ID[] | null;
  taskSortingMode: SortingMode;
  filterDocument: FilterDocument;
};

export type LinkedItemCustomTaskOrderReorderPatch = {
  id: ID;
  customTaskOrder: LinkedItemCustomTaskOrder;
  taskSortingMode: SortingMode;
  filterDocument: FilterDocument;
};

export type BuildSectionReorderPatchOptions = {
  container: LinkedItemReorderContainer;
  updatedSections: ReorderSectionUpdate[];
  // The root pseudo-section carries the section order.
  rootSectionId: ID;
  // The "no section" bucket carries the flat taskSorting.
  noGroupingSectionId: ID;
};

export type BuildCustomTaskOrderReorderPatchOptions = {
  container: LinkedItemReorderContainer;
  updatedSections: ReorderSectionUpdate[];
  groupByMode: GroupByMode;
  rootSectionId: ID;
};

export const withCustomSortFilterDocument = (
  container: Pick<LinkedItemReorderContainer, 'filterDocument'>,
): FilterDocument => ({
  ...(container.filterDocument ?? {}),
  sortingMode: SortingMode.Custom,
});

export const buildSectionReorderPatch = ({
  container,
  updatedSections,
  rootSectionId,
  noGroupingSectionId,
}: BuildSectionReorderPatchOptions): LinkedItemSectionReorderPatch => {
  const sectionMap = new Map(
    (container.sections ?? []).map((section) => [section.id, section]),
  );

  const newSections: LinkedItemSection[] = [];
  let rootOrder = container.sections?.map(({ id }) => id) ?? [];

  updatedSections.forEach((updatedSection) => {
    if (updatedSection.id === rootSectionId) {
      rootOrder = updatedSection.taskSorting;
      return;
    }

    const originalSection = sectionMap.get(updatedSection.id);
    if (originalSection) {
      newSections.push({
        ...originalSection,
        tasks: updatedSection.taskSorting,
      });
      sectionMap.delete(updatedSection.id);
    }
  });

  sectionMap.forEach((remainingSection) => newSections.push(remainingSection));

  newSections.sort((a, b) => rootOrder.indexOf(a.id) - rootOrder.indexOf(b.id));

  return {
    id: container.id,
    sections: newSections,
    taskSorting:
      updatedSections.find((s) => s.id === noGroupingSectionId)?.taskSorting ??
      container.taskSorting ??
      null,
    taskSortingMode: SortingMode.Custom,
    filterDocument: withCustomSortFilterDocument(container),
  };
};

export const buildCustomTaskOrderReorderPatch = ({
  container,
  updatedSections,
  groupByMode,
  rootSectionId,
}: BuildCustomTaskOrderReorderPatchOptions): LinkedItemCustomTaskOrderReorderPatch | null => {
  // The read side resolves None and LifeArea to Section, so a write here is never read back.
  if (
    groupByMode === GroupByMode.None ||
    groupByMode === GroupByMode.LifeArea
  ) {
    return null;
  }

  const groupBySorting = updatedSections.reduce<Record<ID, ID[]>>(
    (acc, section) => {
      if (section.id === rootSectionId) {
        return acc;
      }
      acc[section.id] = section.taskSorting;
      return acc;
    },
    {},
  );

  if (!Object.keys(groupBySorting).length) {
    return null;
  }

  return {
    id: container.id,
    customTaskOrder: {
      ...(container.customTaskOrder ?? {}),
      [groupByMode]: {
        ...(container.customTaskOrder?.[groupByMode] ?? {}),
        ...groupBySorting,
      },
    },
    taskSortingMode: SortingMode.Custom,
    filterDocument: withCustomSortFilterDocument(container),
  };
};
